import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ContactMethods = () => {
  const contactMethods = [
    {
      icon: 'Mail',
      title: 'Email',
      subtitle: 'For detailed inquiries',
      value: 'adrian@portfoliopro.dev',
      description: 'Best for project proposals, detailed questions, and formal communications.',
      responseTime: '24 hours',
      action: 'mailto:adrian@portfoliopro.dev',
      actionText: 'Send Email'
    },
    {
      icon: 'Linkedin',
      title: 'LinkedIn',
      subtitle: 'Professional networking',
      value: '/in/adrian-dev',
      description: 'Connect for professional networking, industry discussions, and career opportunities.',
      responseTime: '48 hours',
      action: 'https://www.linkedin.com/in/ebesoh-adrian/?lipi=urn%3Ali%3Apage%3Ad_flagship3_notifications%3BdjfRJpS4QJGg15S2vM9HJw%3D%3D',
      actionText: 'Connect'
    },
    {
      icon: 'MessageCircle',
      title: 'CodersHub Community',
      subtitle: 'Join the discussion',
      value: '2,500+ members',
      description: 'Join our thriving community for peer support, code reviews, and learning together.',
      responseTime: 'Real-time',
      action: 'https://codershubsite.netlify.app',
      actionText: 'Join Community'
    },
    {
      icon: 'Phone',
      title: 'Emergency Contact',
      subtitle: 'Urgent matters only',
      value: '+237 (678) 708-736',
      description: 'For existing clients with urgent technical issues or time-sensitive matters.',
      responseTime: '2 hours',
      action: 'tel:+237678708736',
      actionText: 'Call Now'
    }
  ];

  const socialLinks = [
    { icon: 'Github', url: 'https://github.com/Ebesoh-Adrian', label: 'GitHub' },
    { icon: 'Twitter', url: 'https://x.com/adrianebesoh237', label: 'Twitter' },
    { icon: 'Youtube', url: 'https://www.youtube.com/@ebesohadrian', label: 'YouTube' },
    { icon: 'Instagram', url: 'https://www.instagram.com/ebesohadriantaleh?igsh=MTFuMjBtOXcwNGgzbg== ', label: 'Instagram' }
  ];

  const officeHours = [
    { day: 'Monday - Friday', hours: '9:00 AM - 6:00 PM EST' },
    { day: 'Saturday', hours: '10:00 AM - 2:00 PM EST' },
    { day: 'Sunday', hours: 'Emergency only' }
  ];

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Multiple Ways to Connect
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Choose the communication method that works best for your needs and timeline.
          </p>
        </div>

        {/* Contact Methods Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {contactMethods?.map((method, index) => (
            <div key={index} className="bg-card rounded-xl border border-border p-6 hover:shadow-elevated transition-all duration-300">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name={method?.icon} size={24} className="text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">{method?.title}</h3>
                  <p className="text-sm text-muted-foreground">{method?.subtitle}</p>
                </div>
              </div>

              <div className="mb-4">
                <p className="font-medium text-foreground mb-2">{method?.value}</p>
                <p className="text-sm text-muted-foreground leading-relaxed">{method?.description}</p>
              </div>

              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <Icon name="Clock" size={16} className="text-success" />
                  <span className="text-sm text-muted-foreground">~{method?.responseTime}</span>
                </div>
              </div>

              <Button
                variant="outline"
                fullWidth
                onClick={() => window.open(method?.action, '_blank')}
                iconName="ExternalLink"
                iconPosition="right"
                size="sm"
              >
                {method?.actionText}
              </Button>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Office Hours */}
          <div className="bg-card rounded-xl border border-border p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                <Icon name="Clock" size={20} className="text-success" />
              </div>
              <h3 className="text-xl font-semibold text-foreground">Office Hours</h3>
            </div>

            <div className="space-y-3">
              {officeHours?.map((schedule, index) => (
                <div key={index} className="flex justify-between items-center py-2 border-b border-border last:border-b-0">
                  <span className="text-foreground font-medium">{schedule?.day}</span>
                  <span className="text-muted-foreground">{schedule?.hours}</span>
                </div>
              ))}
            </div>

            <div className="mt-6 p-4 bg-success/5 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <div className="w-3 h-3 bg-success rounded-full animate-pulse-gold"></div>
                <span className="text-sm font-medium text-success">Currently Available</span>
              </div>
              <p className="text-sm text-muted-foreground">
                I'm online and ready to help with your inquiries.
              </p>
            </div>
          </div>

          {/* Social Media */}
          <div className="bg-card rounded-xl border border-border p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-cta/10 rounded-lg flex items-center justify-center">
                <Icon name="Share2" size={20} className="text-cta" />
              </div>
              <h3 className="text-xl font-semibold text-foreground">Follow My Journey</h3>
            </div>

            <div className="space-y-3 mb-6">
              {socialLinks?.map((social, index) => (
                <a
                  key={index}
                  href={social?.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-3 p-3 rounded-lg hover:bg-muted/50 transition-colors duration-200"
                >
                  <Icon name={social?.icon} size={20} className="text-muted-foreground" />
                  <span className="text-foreground font-medium">{social?.label}</span>
                  <Icon name="ExternalLink" size={16} className="text-muted-foreground ml-auto" />
                </a>
              ))}
            </div>

            <div className="p-4 bg-muted/30 rounded-lg">
              <p className="text-sm text-muted-foreground">
                Stay updated with my latest projects, tutorials, and insights from the tech community.
              </p>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="bg-card rounded-xl border border-border p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                <Icon name="TrendingUp" size={20} className="text-accent" />
              </div>
              <h3 className="text-xl font-semibold text-foreground">Response Stats</h3>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Average Response Time</span>
                <span className="font-semibold text-success">18 hours</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Project Inquiries</span>
                <span className="font-semibold text-primary">95% response rate</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Mentorship Requests</span>
                <span className="font-semibold text-cta">100% response rate</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Community Support</span>
                <span className="font-semibold text-conversion">24/7 active</span>
              </div>
            </div>

            <div className="mt-6 p-4 bg-primary/5 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Award" size={16} className="text-primary" />
                <span className="text-sm font-medium text-primary">Quality Guarantee</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Every inquiry receives a thoughtful, personalized response.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactMethods;